import * as React from 'react';
import { observer } from 'mobx-react';

import { ButtonSize } from 'shared/newEntities/components/Button';
import { ComponentType } from 'shared/entities/components/Component';
import { WazzupCreationStore } from 'stores/channelCreationStores';
import { useRootStore } from 'stores/index';
import { WazzupTokenKind } from 'shared/entities/wazzup';
import { useChannelsModalStore } from 'stores/ChannelsModalStore';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { Button } from 'shared/newComponents/buttons';
import { Input } from 'shared/newComponents/form';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import ExternalLink from 'shared/newComponents/ExternalLink';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { wazzupLinks } from 'shared/entities/app';
import Typography from 'shared/newComponents/Typography';

import '../Wazzup.modules.scss';

type Props = {
  store: WazzupCreationStore;
};

const WithAccountAndCRM: React.FC<Props> = ({ store }) => {
  const rootStore = useRootStore();
  const { store: modalStore } = useChannelsModalStore();

  React.useEffect(() => {
    if (store.tokenKind.value !== WazzupTokenKind.sidecar) {
      store.reset();
      store.tokenKind.changeValue(WazzupTokenKind.sidecar);
    }
  }, []);

  const handleCreate = React.useCallback(async () => {
    const response = await store.create();
    if (!response.isError) {
      if (modalStore.selectedViaWazzupChannelKind) {
        rootStore.analyticsStore.sendEvent(
          AnalyticsEvent.channelCreationViaWazzupOk,
          {
            created_channel_kind_via_wazzup:
              modalStore.selectedViaWazzupChannelKind
          }
        );
      }
      modalStore.handleChannelCreate(response.data.channelId);
    }
  }, [modalStore.selectedViaWazzupChannelKind]);

  return (
    <Typography
      styleName="wazzup-case"
      fontType={TypographyType.text}
      fontSize={TypographySize.s}
      fontWeight={TypographyWeight.normal}
    >
      <TypedTransComponent
        ns="newContainers"
        i18nKey="ChannelsModal.ChannelForm.Wazzup.withAccountAndCRM.instruction"
      >
        Получите ключ API по{' '}
        <ExternalLink
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontColor={TypographyColor.link}
          underline
          href={wazzupLinks.howConnect}
        >
          инструкции
        </ExternalLink>
        . 1. Зайдите в&nbsp;раздел «Интеграция с&nbsp;CRM»
        <br />
        2. В&nbsp;подразделе «Дополнительно» найдите «Ключ API»
        <br />
        3. Скопируйте токен и вставьте его в поле ниже.
      </TypedTransComponent>
      <Input
        styleName="wazzup-case__input"
        label={(t) =>
          t('ChannelsModal.ChannelForm.Wazzup.withAccountAndCRM.input.label', {
            ns: 'newContainers'
          })
        }
        placeholder={(t) =>
          t(
            'ChannelsModal.ChannelForm.Wazzup.withAccountAndCRM.input.placeholder',
            {
              ns: 'newContainers'
            }
          )
        }
        value={store.token.value}
        onChange={store.token.changeValue}
        error={store.token.error}
        onBlur={store.token.validate}
      />
      <Button
        styleName="wazzup-case__button"
        size={ButtonSize.m}
        type={ComponentType.button}
        onClick={handleCreate}
        isLoading={store.creationStage.isLoading}
        disabled={store.isError}
        fullWidth
      >
        {(t) =>
          t('ChannelsModal.ChannelForm.button', {
            ns: 'newContainers'
          })
        }
      </Button>
    </Typography>
  );
};

export default observer(WithAccountAndCRM);
